import { Arr } from '../../../CodesForFoundation';
import { ObjectManager } from '../../../concepts/objects';
import { ColorGroup } from '../../../ui';
import { EmojiStore } from '../../../ui/emoji';
import { Physical, RelativeTo } from '../../canvas/entities/Physical';
import { Physicals } from '../../canvas/entities/Physicals';
import { SpatialIndex } from '../../canvas/utilities/SpatialIndex';
import { ActiveObject, resolve } from '../../Container';
import { JiraCard } from '../atlassian/JiraCard';
import { IndexCard } from '../paper/IndexCard';
import { Sticky } from '../paper/Sticky';
import { TaskCard } from '../paper/TaskCard';
import { Zone } from './Zone';
import { ZoneAssocation } from './ZoneAssociations';

export class ZoneMechanics extends ActiveObject {

    @resolve(ObjectManager) objects!: ObjectManager;
    @resolve(SpatialIndex) spatialIndex!: SpatialIndex;

    public start(): void {
        const { objects } = this;
        this.burden(
            objects.createTrigger('Zone.OnCreateSetRandomEmoji')
                .for(Zone).onCreated()
                .doForEach(this.doSetRandomEmojiOnCreate.bind(this)),
            objects.createTrigger('Zone.OnZoneColorChange')
                .for(Zone).onChangesTo('autoColor')
                .doForEach(this.doZoneAssignmentOnZoneChange.bind(this)),
            objects.createTrigger('Zone.OnStickyMove')
                .for(Sticky).onChangesTo('x', 'y')
                .do(this.doZoneAssignment.bind(this)),
            objects.createTrigger('Zone.OnTaskCardMove')
                .for(TaskCard).onChangesTo('x', 'y')
                .do(this.doZoneAssignment.bind(this)),
            objects.createTrigger('Zone.OnIndexCardMove')
                .for(IndexCard).onChangesTo('x', 'y')
                .do(this.doZoneAssignment.bind(this)),
            objects.createTrigger('Zone.OnJiraCardMove')
                .for(JiraCard).onChangesTo('x', 'y')
                .do(this.doZoneAssignment.bind(this)),
        );
    }

    private doSetRandomEmojiOnCreate(zone: Zone) {
        if (zone.emoji) return;
        zone.emoji = Arr.random(EmojiStore.getEmoji())?.id;
    }

    private doZoneAssignmentOnZoneChange(zone: Zone) {

        const zpb = zone.bounds(RelativeTo.Model).inflate([75, 75]);
        const results = this.spatialIndex.lookup(zpb);
        const hits = Array.from(results).filter(x =>
            (x.is(Sticky) || x.is(TaskCard) || x.is(IndexCard) || x.is(JiraCard)) &&
            Physicals.isAbove(x, zone));

        this.doZoneAssignment(hits as Sticky[]);
    }

    private doZoneAssignment(items: (Sticky | TaskCard | IndexCard | JiraCard)[]) {
        for (const tm of items) {
            const z = this.findNearestZoneForObject(tm);
            if (tm.has('autoColor')) {
                if (z?.autoColor) {
                    tm.setMaybe('autoColor', ColorGroup.findPeer(z.autoColor, 4));
                }
                else {
                    tm.setMaybe('autoColor', '');
                }
            }
            ZoneAssocation.setMaybe(tm, z?.id || null);
        }
    }

    private findNearestZoneForObject(subject: Physical): Zone | null {
        // Not mounted? Can't be in a zone...
        if (!subject.mounted()) return null;

        const sb = subject.bounds(RelativeTo.Model);
        const zones = Physicals.bottomToTop(Physicals.polarize(this.spatialIndex.lookup(sb.scale(2)), Zone));

        // Narrow options down
        const opts = zones.filter(z => Physicals.isAbove(subject, z) && sb.intersects(z.bounds(RelativeTo.Model)));
        if (opts.length <= 1) return opts[0] ?? null;

        // If more than one, choose the one the center is over
        const over = zones.find(z => z.bounds(RelativeTo.Model).contains(sb.center()));
        if (over) return over;

        // Otherwise, choose nearest
        zones.sort((a, b) => a.center(RelativeTo.Model).distanceTo(sb.center()) - b.center(RelativeTo.Model).distanceTo(sb.center()));
        return zones[0];
    }
}
